import { AnimatePresence, m } from 'motion/react';
import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import { cn } from '../lib/cn';

import { menuItemStyles } from './styles';

/**
 * Un menú desplegable accesible, sin Radix.
 *
 * `13 §1` preveía shadcn/ui y el producto nunca lo incorporó; traerlo entero
 * ahora, para los tres menús que hay, sería pagar el presupuesto de `13 §8` por
 * un componente. Esto cubre lo que el producto usa: se cierra con Escape, se
 * cierra al pulsar fuera, devuelve el foco al disparador y anuncia su estado
 * con `aria-expanded`.
 *
 * Lo que deliberadamente NO hace es capturar el foco ni implementar la
 * navegación con flechas del patrón `menu` de ARIA. Por eso los elementos son
 * botones y enlaces normales dentro de un contenedor sin `role="menu"`: un rol
 * a medias es peor que ninguno, porque promete al lector de pantalla una
 * interacción que luego no existe.
 */
export function Menu({
  trigger,
  children,
  align = 'end',
  className,
}: {
  trigger: (props: { open: boolean; toggle: () => void }) => ReactNode;
  children: (props: { close: () => void }) => ReactNode;
  align?: 'start' | 'end';
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        // Sin esto, cerrar con Escape deja el foco en el documento y quien
        // navega con teclado tiene que volver a tabular desde el principio.
        root.current?.querySelector('button')?.focus();
      }
    };

    const onPointer = (event: PointerEvent) => {
      if (root.current && !root.current.contains(event.target as globalThis.Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointer);

    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointer);
    };
  }, [open]);

  return (
    <div ref={root} className="relative">
      {trigger({ open, toggle: () => setOpen((value) => !value) })}

      <AnimatePresence>
        {open ? (
          <m.div
            initial={{ opacity: 0, y: -6, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.97, transition: { duration: 0.12 } }}
            transition={{ duration: 0.18, ease: [0.22, 0.8, 0.28, 1] }}
            className={cn(
              'absolute top-[calc(100%+8px)] z-50 min-w-56 origin-top overflow-hidden rounded-lg border border-line bg-raised p-1.5 shadow-lg',
              align === 'end' ? 'right-0' : 'left-0',
              className,
            )}
          >
            {children({ close: () => setOpen(false) })}
          </m.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

export function MenuItem({
  icon,
  active = false,
  className,
  children,
  ...props
}: {
  icon?: ReactNode;
  active?: boolean;
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button type="button" {...props} className={menuItemStyles(active, className)}>
      {icon ? <span className="shrink-0 [&>svg]:size-4">{icon}</span> : null}
      <span className="grow truncate">{children}</span>
    </button>
  );
}

export function MenuLabel({ children }: { children: ReactNode }) {
  return <p className="px-2.5 pt-2 pb-1 text-[0.62rem] font-bold tracking-widest text-ink-faint uppercase">{children}</p>;
}

export function MenuSeparator() {
  return <hr className="my-1.5 border-line" />;
}
